import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Copy, DollarSign, MousePointerClick, ShoppingBag, TrendingUp, LogOut, Check, AlertCircle, Clock, CheckCircle, Percent, ArrowUpRight, Link2, Instagram, Globe, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SITE_URL = window.location.origin;

const AffiliateDashboardPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showPayout, setShowPayout] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [payoutForm, setPayoutForm] = useState({ amount: '' });
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [postsThisWeek, setPostsThisWeek] = useState(0);
  const [postForm, setPostForm] = useState({ post_url: '', platform: 'instagram', notes: '' });
  const [postLoading, setPostLoading] = useState(false);

  const fetchDashboard = async () => {
    try {
      const { data: d } = await axios.get(`${API}/affiliate/dashboard`, { withCredentials: true });
      setData(d);
      setPayoutForm(f => ({ ...f, payment_details: '' }));
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/affiliate');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const { data: d } = await axios.get(`${API}/affiliate/my-posts`, { withCredentials: true });
      setPosts(d.posts || []);
      setPostsThisWeek(d.posts_this_week || 0);
    } catch (err) {
      console.warn('Failed to fetch affiliate posts:', err?.response?.status);
    }
  };

  const submitPost = async (e) => {
    e.preventDefault();
    setPostLoading(true);
    try {
      await axios.post(`${API}/affiliate/submit-post`, postForm, { withCredentials: true });
      toast.success('Post submitted for review!');
      setPostForm({ post_url: '', platform: 'instagram', notes: '' });
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit post');
    } finally {
      setPostLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${SITE_URL}/?ref=${data.ref_code}`);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePayout = async (e) => {
    e.preventDefault();
    setPayoutLoading(true);
    try {
      const { data: res } = await axios.post(`${API}/affiliate/payout-request`, {
        amount: parseFloat(payoutForm.amount),
      }, { withCredentials: true });
      toast.success(res.message);
      setShowPayout(false);
      setPayoutForm({ amount: '' });
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payout request failed');
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/affiliate/logout`, {}, { withCredentials: true });
    navigate('/affiliate');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <div className="text-center max-w-md" data-testid="affiliate-dashboard-error">
          <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Unable to load dashboard
          </h2>
          <p className="text-[#A1A7B5] mb-6 text-sm">
            We couldn't fetch your affiliate data. This usually means your session expired or your account isn't approved yet.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => window.location.reload()} className="btn-secondary" data-testid="aff-retry-btn">Retry</Button>
            <Button onClick={() => navigate('/affiliate')} className="btn-primary" data-testid="aff-back-btn">Back to Login</Button>
          </div>
        </div>
      </div>
    );
  }

  const referralLink = `${SITE_URL}/?ref=${data.ref_code}`;
  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    confirmed: 'bg-green-500/20 text-green-400',
    approved: 'bg-green-500/20 text-green-400',
    rejected: 'bg-red-500/20 text-red-400',
  };

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="affiliate-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Affiliate Dashboard
            </h1>
            <p className="text-[#A1A7B5] mt-1">Welcome, {data.name}</p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/affiliate/tax-info" className="text-[#A1A7B5] hover:text-[#00F0FF] text-sm transition-colors" data-testid="tax-info-link">
              Payouts &amp; Tax Info
            </Link>
            <button onClick={handleLogout} className="flex items-center space-x-2 text-[#A1A7B5] hover:text-white transition-colors" data-testid="affiliate-logout">
              <LogOut className="w-5 h-5" />
              <span className="text-sm">Logout</span>
            </button>
          </div>
        </div>

        {/* Referral Link */}
        <div className="glass-card p-6 mb-8">
          <p className="text-[#A1A7B5] text-sm mb-3">Your Referral Link</p>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-[#111621] rounded-lg px-4 py-3 text-[#00F0FF] text-sm font-mono truncate border border-[#1E2532]" data-testid="referral-link">
              {referralLink}
            </div>
            <Button onClick={copyLink} className="bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 text-[#00F0FF] px-4 py-3 flex-shrink-0" data-testid="copy-link-btn">
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </Button>
          </div>
          <p className="text-[#A1A7B5] text-xs mt-2">Ref Code: <span className="text-white font-mono">{data.ref_code}</span> &middot; Commission: <span className="text-[#00F0FF]">{(data.commission_rate * 100).toFixed(0)}%</span></p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex mb-8 bg-[#0C0E14] rounded-lg p-1 border border-[#1E2532]">
          {['overview', 'activity', 'commissions', 'payouts'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors capitalize ${activeTab === tab ? 'bg-[#00F0FF] text-black' : 'text-[#A1A7B5] hover:text-white'}`}
              data-testid={`tab-${tab}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Main Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="glass-card p-5" data-testid="stat-clicks">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <MousePointerClick className="w-5 h-5 text-blue-400" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-white">{data.total_clicks}</p>
                <p className="text-[#A1A7B5] text-sm">Total Clicks</p>
              </div>
              <div className="glass-card p-5" data-testid="stat-sales">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-green-400" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-white">{data.total_sales}</p>
                <p className="text-[#A1A7B5] text-sm">Total Sales</p>
              </div>
              <div className="glass-card p-5" data-testid="stat-earned">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-[#00F0FF]" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-white">${data.total_earned.toFixed(2)}</p>
                <p className="text-[#A1A7B5] text-sm">Total Earned</p>
              </div>
              <div className="glass-card p-5" data-testid="stat-balance">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-purple-400" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-white">${data.balance.toFixed(2)}</p>
                <p className="text-[#A1A7B5] text-sm">Available Balance</p>
              </div>
            </div>

            {/* Commission Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="glass-card p-5 border-l-4 border-yellow-400" data-testid="pending-commission">
                <div className="flex items-center justify-between mb-2">
                  <Clock className="w-5 h-5 text-yellow-400" />
                  <span className="text-xs text-yellow-400 bg-yellow-500/10 px-2 py-0.5 rounded-full">{data.pending_count} orders</span>
                </div>
                <p className="text-xl font-bold text-white">${data.pending_commission.toFixed(2)}</p>
                <p className="text-[#A1A7B5] text-sm">Pending Commission</p>
              </div>
              <div className="glass-card p-5 border-l-4 border-green-400" data-testid="confirmed-commission">
                <div className="flex items-center justify-between mb-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">{data.confirmed_count} orders</span>
                </div>
                <p className="text-xl font-bold text-white">${data.confirmed_commission.toFixed(2)}</p>
                <p className="text-[#A1A7B5] text-sm">Confirmed Commission</p>
              </div>
              <div className="glass-card p-5 border-l-4 border-[#00F0FF]" data-testid="conversion-rate">
                <div className="flex items-center justify-between mb-2">
                  <Percent className="w-5 h-5 text-[#00F0FF]" />
                  <ArrowUpRight className="w-4 h-4 text-[#00F0FF]" />
                </div>
                <p className="text-xl font-bold text-white">{data.conversion_rate}%</p>
                <p className="text-[#A1A7B5] text-sm">Conversion Rate</p>
              </div>
            </div>

            {/* Recent Referrals */}
            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Recent Referrals</h3>
              {data.referrals.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-8 h-8 text-[#A1A7B5] mx-auto mb-2" />
                  <p className="text-[#A1A7B5] text-sm">No referrals yet. Share your link to start earning!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {data.referrals.slice(0, 10).map((ref) => (
                    <div key={ref.id} className="flex items-center justify-between p-3 bg-[#111621] rounded-lg" data-testid={`referral-${ref.id}`}>
                      <div>
                        <p className="text-white text-sm">Order #{ref.order_id?.slice(-8)}</p>
                        <p className="text-[#A1A7B5] text-xs">{new Date(ref.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${statusColors[ref.status] || 'bg-[#1E2532] text-[#A1A7B5]'}`}>
                          {ref.status}
                        </span>
                        <div className="text-right">
                          <p className="text-[#00F0FF] font-semibold">${ref.commission?.toFixed(2)}</p>
                          <p className="text-[#A1A7B5] text-xs">from ${ref.order_total?.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="space-y-6">
            {/* Weekly Status */}
            <div className={`glass-card p-6 border ${postsThisWeek >= 1 ? 'border-green-500/30' : 'border-yellow-500/30'}`} data-testid="weekly-compliance">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>This Week's Activity</h3>
                <span className={`text-xs px-3 py-1 rounded-full font-semibold ${postsThisWeek >= 1 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                  {postsThisWeek >= 1 ? 'Compliant' : 'Post Required'}
                </span>
              </div>
              <p className="text-[#A1A7B5] text-sm">{postsThisWeek} of 1 required post{postsThisWeek !== 1 ? 's' : ''} submitted this week</p>
              <div className="w-full bg-[#111621] rounded-full h-2 mt-3">
                <div className={`h-2 rounded-full transition-all ${postsThisWeek >= 1 ? 'bg-green-400' : 'bg-yellow-400'}`} style={{ width: `${Math.min(postsThisWeek / 1 * 100, 100)}%` }} />
              </div>
            </div>

            {/* Submit Post */}
            <div className="glass-card p-6" data-testid="submit-post-form">
              <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Submit a Post</h3>
              <form onSubmit={submitPost} className="space-y-4">
                <div>
                  <label className="text-[#A1A7B5] text-sm mb-1 block">Post URL *</label>
                  <div className="relative">
                    <input type="url" value={postForm.post_url} onChange={(e) => setPostForm({ ...postForm, post_url: e.target.value })} placeholder="https://instagram.com/p/..." required className="input-field w-full pl-10" data-testid="post-url-input" />
                    <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
                  </div>
                </div>
                <div>
                  <label className="text-[#A1A7B5] text-sm mb-1 block">Platform</label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { id: 'instagram', label: 'Instagram', icon: Instagram },
                      { id: 'tiktok', label: 'TikTok', icon: Globe },
                      { id: 'youtube', label: 'YouTube', icon: Globe },
                      { id: 'twitter', label: 'X/Twitter', icon: Globe },
                    ].map(p => (
                      <button key={p.id} type="button" onClick={() => setPostForm({ ...postForm, platform: p.id })}
                        className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${postForm.platform === p.id ? 'bg-[#00F0FF]/15 border border-[#00F0FF] text-[#00F0FF]' : 'bg-[#111621] border border-[#1E2532] text-[#A1A7B5]'}`}
                        data-testid={`post-platform-${p.id}`}>
                        <p.icon className="w-3.5 h-3.5" />{p.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[#A1A7B5] text-sm mb-1 block">Notes (optional)</label>
                  <input value={postForm.notes} onChange={(e) => setPostForm({ ...postForm, notes: e.target.value })} placeholder="e.g. Product spotlight on BPC-157" className="input-field w-full" data-testid="post-notes-input" />
                </div>
                <Button type="submit" disabled={postLoading} className="btn-primary flex items-center gap-2" data-testid="submit-post-btn">
                  <Send className="w-4 h-4" />
                  {postLoading ? 'Submitting...' : 'Submit Post'}
                </Button>
              </form>
            </div>

            {/* Post History */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Post History</h3>
              {posts.length === 0 ? (
                <div className="text-center py-8">
                  <Instagram className="w-8 h-8 text-[#A1A7B5] mx-auto mb-2" />
                  <p className="text-[#A1A7B5] text-sm">No posts submitted yet. Share your first post!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {posts.map((post) => (
                    <div key={post.id} className="flex items-center justify-between p-3 bg-[#111621] rounded-lg" data-testid={`post-${post.id}`}>
                      <div className="flex-1 min-w-0 mr-4">
                        <a href={post.post_url} target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] text-sm hover:underline truncate block">{post.post_url}</a>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[#A1A7B5] text-xs capitalize">{post.platform}</span>
                          <span className="text-[#A1A7B5] text-xs">{new Date(post.created_at).toLocaleDateString()}</span>
                          {post.notes && <span className="text-[#A1A7B5] text-xs truncate">{post.notes}</span>}
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full capitalize flex-shrink-0 ${post.status === 'approved' ? 'bg-green-500/20 text-green-400' : post.status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                        {post.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Commissions Tab */}
        {activeTab === 'commissions' && (
          <div className="space-y-6">
            {/* Commission Summary */}
            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Commission Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-[#111621] rounded-lg">
                  <p className="text-2xl font-bold text-[#00F0FF]">${data.total_earned.toFixed(2)}</p>
                  <p className="text-[#A1A7B5] text-xs mt-1">Lifetime Earnings</p>
                </div>
                <div className="text-center p-4 bg-[#111621] rounded-lg">
                  <p className="text-2xl font-bold text-yellow-400">${data.pending_commission.toFixed(2)}</p>
                  <p className="text-[#A1A7B5] text-xs mt-1">Pending</p>
                </div>
                <div className="text-center p-4 bg-[#111621] rounded-lg">
                  <p className="text-2xl font-bold text-green-400">${data.confirmed_commission.toFixed(2)}</p>
                  <p className="text-[#A1A7B5] text-xs mt-1">Confirmed</p>
                </div>
                <div className="text-center p-4 bg-[#111621] rounded-lg">
                  <p className="text-2xl font-bold text-purple-400">${data.total_paid.toFixed(2)}</p>
                  <p className="text-[#A1A7B5] text-xs mt-1">Total Paid Out</p>
                </div>
              </div>
            </div>

            {/* All Referrals */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>All Referral Sales</h3>
              {data.referrals.length === 0 ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-10 h-10 text-[#A1A7B5] mx-auto mb-3" />
                  <p className="text-[#A1A7B5]">No commissions earned yet.</p>
                  <p className="text-[#A1A7B5] text-sm mt-1">Share your referral link to start earning 15% per sale!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[#A1A7B5] border-b border-[#1E2532]">
                        <th className="pb-3 pr-4">Order</th>
                        <th className="pb-3 pr-4">Date</th>
                        <th className="pb-3 pr-4">Order Total</th>
                        <th className="pb-3 pr-4">Commission (15%)</th>
                        <th className="pb-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1E2532]">
                      {data.referrals.map((ref) => (
                        <tr key={ref.id} className="text-white">
                          <td className="py-3 pr-4 font-mono text-xs">#{ref.order_id?.slice(-8)}</td>
                          <td className="py-3 pr-4 text-[#A1A7B5]">{new Date(ref.created_at).toLocaleDateString()}</td>
                          <td className="py-3 pr-4">${ref.order_total?.toFixed(2)}</td>
                          <td className="py-3 pr-4 text-[#00F0FF] font-semibold">${ref.commission?.toFixed(2)}</td>
                          <td className="py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${statusColors[ref.status] || 'bg-[#1E2532] text-[#A1A7B5]'}`}>
                              {ref.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payouts Tab */}
        {activeTab === 'payouts' && (
          <div className="space-y-6">
            {/* Payout Summary */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Payouts</h3>
                <div className="text-right">
                  <p className="text-sm text-[#A1A7B5]">Available Balance</p>
                  <p className="text-2xl font-bold text-[#00F0FF]">${data.balance.toFixed(2)}</p>
                </div>
              </div>

              {/* Request Payout */}
              {!showPayout ? (
                <Button onClick={() => setShowPayout(true)} className="btn-primary" disabled={data.balance < 25} data-testid="request-payout-btn">
                  {data.balance < 25 ? `Minimum payout: $25.00 (Balance: $${data.balance.toFixed(2)})` : 'Request Payout'}
                </Button>
              ) : (
                <div className="bg-[#111621] rounded-lg p-6 border border-[#1E2532]">
                  <h4 className="text-white font-semibold mb-1">Request Payout</h4>
                  <p className="text-[#A1A7B5] text-sm mb-4">Payouts are sent securely via Stripe direct deposit (2 business days). Funds arrive in the bank account you linked during tax/payout setup.</p>
                  {!data.stripe_payouts_enabled && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4" data-testid="payout-stripe-required">
                      <p className="text-yellow-300 text-sm font-semibold mb-1">Set up payouts first</p>
                      <p className="text-[#A1A7B5] text-xs mb-3">You need to complete your Stripe payout &amp; tax info before you can request a payout. This keeps your commissions compliant and ensures your 1099 is issued automatically at year-end.</p>
                      <Link to="/affiliate/tax-info" className="inline-block bg-[#00F0FF] text-black text-xs font-bold px-4 py-2 rounded-lg hover:bg-[#00D8E6] transition-colors" data-testid="go-to-tax-info">
                        Complete Setup &rarr;
                      </Link>
                    </div>
                  )}
                  <form onSubmit={handlePayout} className="space-y-4 max-w-md">
                    <div>
                      <label className="text-[#A1A7B5] text-sm mb-1 block">Amount (max: ${data.balance.toFixed(2)})</label>
                      <input type="number" step="0.01" min="25" max={data.balance} value={payoutForm.amount} onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })} placeholder="25.00" required disabled={!data.stripe_payouts_enabled} className="input-field w-full disabled:opacity-40 disabled:cursor-not-allowed" data-testid="payout-amount" />
                    </div>
                    <div className="flex space-x-3">
                      <Button type="submit" disabled={payoutLoading || !data.stripe_payouts_enabled} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed" data-testid="submit-payout-btn">
                        {payoutLoading ? 'Submitting...' : 'Submit Request'}
                      </Button>
                      <Button type="button" onClick={() => setShowPayout(false)} className="bg-[#1E2532] hover:bg-[#2a3040] text-white">
                        Cancel
                      </Button>
                    </div>
                  </form>
                </div>
              )}
            </div>

            {/* Payout History */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Payout History</h3>
              {data.payouts.length === 0 ? (
                <div className="text-center py-12">
                  <DollarSign className="w-10 h-10 text-[#A1A7B5] mx-auto mb-3" />
                  <p className="text-[#A1A7B5]">No payout requests yet.</p>
                  <p className="text-[#A1A7B5] text-sm mt-1">Earn commissions and request payouts when your balance reaches $25.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {data.payouts.map((payout) => (
                    <div key={payout.id} className="flex items-center justify-between p-4 bg-[#111621] rounded-lg" data-testid={`payout-${payout.id}`}>
                      <div>
                        <p className="text-white font-medium">${payout.amount.toFixed(2)}</p>
                        <p className="text-[#A1A7B5] text-xs">{payout.payment_method} - {payout.payment_details}</p>
                        <p className="text-[#A1A7B5] text-xs mt-1">{new Date(payout.created_at).toLocaleDateString()}</p>
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full capitalize ${statusColors[payout.status] || 'bg-[#1E2532] text-[#A1A7B5]'}`}>
                        {payout.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateDashboardPage;
